"use client";

import { LocationCard } from "../self-schedule/components/self-schedule-card";
import { calculateDistance } from "../self-schedule/utils";
import React, { useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import AddressInput from "@/components/self-schedule/address-input";
import { Button } from "@/components/ui";
import { useFrontPage } from "@/hooks/self-schedule/use-front-page";
import { LocationWithVisibility } from "@/types/location";
interface PickupLocationStepProps {
  selectedLocationId: string | null;
  preferredLocationId?: string | null;
  // Set when something in the cart is only sold at some locations.
  allowedLocationIds?: string[] | null;
  onSelectLocation: (locationId: string) => void;
  onBack: () => void;
}

/**
 * Purchases that require a physical clinic location collect that choice before
 * payment so fulfillment, service redemption, and taxes use the location the
 * patient selected.
 */
const PickupLocationStep: React.FC<PickupLocationStepProps> = ({
  selectedLocationId,
  preferredLocationId,
  allowedLocationIds,
  onSelectLocation,
  onBack
}) => {
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const {
    data: frontPageData,
    isLoading
  } = useFrontPage();
  const locations: LocationWithVisibility[] = useMemo(() => {
    if (!frontPageData?.locations) return [];
    return frontPageData.locations.filter(location => !location.isVirtual && !location.isArchived && (!allowedLocationIds || allowedLocationIds.includes(location.id))).map(location_0 => {
      let distance: number | undefined;
      if (userLocation && location_0.latitude && location_0.longitude) {
        distance = calculateDistance(userLocation.lat, userLocation.lng, location_0.latitude, location_0.longitude);
      }
      return {
        ...location_0,
        distance,
        isServiceAvailable: true
      };
    }).sort((a, b) => {
      if (a.distance == null && b.distance == null) return a.name.localeCompare(b.name);
      if (a.distance == null) return 1;
      if (b.distance == null) return -1;
      return a.distance - b.distance;
    });
  }, [frontPageData?.locations, userLocation, allowedLocationIds]);
  const handleAddressChange = (addressData: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude?: number;
    longitude?: number;
  }) => {
    if (addressData.latitude && addressData.longitude) {
      setUserLocation({
        lat: addressData.latitude,
        lng: addressData.longitude
      });
    }
  };
  if (isLoading) {
    return <section className="rounded-2xl border border-border bg-background p-6 shadow-sm sm:p-8">
        <p className="text-body text-foreground-secondary">
          Loading pickup locations…
        </p>
      </section>;
  }
  return <section className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 cursor-pointer px-0 text-foreground-secondary">
        <FaArrowLeft className="mr-2 text-xxs" />
        <span>Back</span>
      </Button>
      <div>
        <h1 className="text-h1 text-foreground">Choose a clinic location</h1>
        <p className="mt-2 text-body text-foreground-secondary">
          Select the clinic location for pickup, fulfillment, or service
          redemption. Applicable taxes will be calculated for this location
          before payment.
        </p>
      </div>

      <AddressInput onAddressChange={handleAddressChange} placeholder="Enter your address to find nearby locations" label="Your Address" className="max-w-md" />

      {locations.length === 0 ? <div className="rounded-xl border border-dashed border-border bg-background-secondary p-5 text-body text-foreground-secondary">
          No pickup locations are available right now.
        </div> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {locations.map((location_1, index) => <LocationCard key={location_1.id} location={location_1} onLocationClick={onSelectLocation} isSelected={selectedLocationId === location_1.id} isPreviousAppointment={preferredLocationId === location_1.id} hideInfoButton animationDelay={index * 100 + 50} />)}
        </div>}

      <p className="text-caption text-foreground-secondary">
        Selecting a location will take you to payment.
      </p>
    </section>;
};
export default PickupLocationStep;